import { useUpdate } from "@refinedev/core";
import {
  Row,
  Col,
  Slider,
  Switch,
  Typography,
  Space,
  Button,
  Descriptions,
  Tag,
  message,
} from "antd";
import { SaveOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import type { FeatureFlag } from "@/lib/types";
import { melColors } from "@/theme/tokens";
import { formatDate } from "@/lib/format";

const { Text, Paragraph } = Typography;

const MARKS = {
  0: "0%",
  25: "25%",
  50: "50%",
  75: "75%",
  100: "100%",
};

/**
 * FlagDetail — painel "show" embutido na linha expandida da tabela de flags.
 *
 * Mostra a descrição completa e permite ajustar o rollout (%) com um slider que
 * persiste via PATCH /admin/feature-flags/:id (useUpdate). Mantemos o ajuste
 * inline aqui (sem rota extra) para um controle fino sem sair da lista; a edição
 * completa fica em /feature-flags/edit/:id.
 */
export const FlagDetail: React.FC<{ flag: FeatureFlag }> = ({ flag }) => {
  const [pct, setPct] = useState<number>(flag.rollout_percent ?? 0);
  const { mutate, isLoading } = useUpdate();

  // Mantém o slider em sincronia se a flag for atualizada por outra ação.
  useEffect(() => {
    setPct(flag.rollout_percent ?? 0);
  }, [flag.rollout_percent]);

  const dirty = pct !== (flag.rollout_percent ?? 0);

  const saveRollout = () => {
    mutate(
      {
        resource: "feature-flags",
        id: flag.id,
        values: { rollout_percent: pct },
        mutationMode: "optimistic",
      },
      {
        onSuccess: () =>
          message.success(`Rollout de ${flag.key} ajustado para ${pct}%`),
      },
    );
  };

  const toggleEnabled = (enabled: boolean) => {
    mutate({
      resource: "feature-flags",
      id: flag.id,
      values: { enabled },
      mutationMode: "optimistic",
    });
  };

  return (
    <Row gutter={[24, 16]} style={{ padding: "4px 8px" }}>
      <Col xs={24} md={10}>
        <Descriptions
          column={1}
          size="small"
          colon={false}
          title={<Text strong>Detalhes</Text>}
        >
          <Descriptions.Item label="Key">
            <code>{flag.key}</code>
          </Descriptions.Item>
          <Descriptions.Item label="Estado">
            {flag.enabled ? (
              <Tag color="cyan">ativa</Tag>
            ) : (
              <Tag>desligada</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Atualizada">
            {formatDate(flag.updated_at)}
          </Descriptions.Item>
        </Descriptions>
        <Paragraph type="secondary" style={{ marginTop: 8 }}>
          {flag.description ?? "Sem descrição cadastrada para esta flag."}
        </Paragraph>
      </Col>

      <Col xs={24} md={14}>
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Space>
            <Text strong>Habilitada</Text>
            <Switch
              checked={flag.enabled}
              checkedChildren="on"
              unCheckedChildren="off"
              loading={isLoading}
              onChange={toggleEnabled}
            />
          </Space>

          <div>
            <Space
              style={{ width: "100%", justifyContent: "space-between" }}
            >
              <Text strong>Rollout</Text>
              <Text style={{ color: melColors.aqua700, fontWeight: 700 }}>
                {pct}%
              </Text>
            </Space>
            <Slider
              min={0}
              max={100}
              step={5}
              marks={MARKS}
              value={pct}
              disabled={!flag.enabled}
              onChange={(v) => setPct(v as number)}
              tooltip={{ formatter: (v) => `${v}%` }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {flag.enabled
                ? "Percentual de usuários elegíveis que veem a funcionalidade."
                : "Ative a flag para configurar o rollout gradual."}
            </Text>
          </div>

          <Button
            type="primary"
            icon={<SaveOutlined />}
            disabled={!dirty || !flag.enabled}
            loading={isLoading}
            onClick={saveRollout}
          >
            Salvar rollout
          </Button>
        </Space>
      </Col>
    </Row>
  );
};
